<template>
  <div class="container mx-auto px-4 py-8 md:py-12">
    <h1 class="text-3xl font-bold mb-8 border-b pb-4">My Favorite Models</h1>

    <div v-if="isLoading" class="text-center py-10">
      <p class="text-muted-foreground">Loading favorites...</p>
    </div>

    <div v-else-if="enrichedFavoriteModels.length > 0" class="space-y-6">
      <!-- List of Favorite Models -->
      <div 
        v-for="item in enrichedFavoriteModels"
        :key="item.id"
        class="bg-card rounded-xl border shadow-xs overflow-hidden flex flex-col sm:flex-row relative"
      >
        <!-- Remove Button (Absolute Position) -->
        <Button 
          variant="destructive"
          size="sm"
          class="absolute top-3 left-3 z-10 h-7 px-2.5 text-xs"
          @click="favoritesStore.removeFavorite(item.id)"
          aria-label="Remove from favorites"
        >
          Remove
        </Button>

        <!-- Image Section -->
        <div class="relative w-full sm:w-1/2 md:w-1/2 aspect-16/9 sm:aspect-4/3 bg-muted/30 overflow-hidden">
          <img 
            v-if="item.modelDetails?.model_image"
            :src="item.modelDetails.model_image" 
            :alt="item.modelDetails.title.rendered"
            class="w-full h-full object-contain bg-muted/10"
          />
          <!-- Placeholder if image missing -->
          <div v-else class="w-full h-full flex items-center justify-center bg-muted">
            <PhotoIcon class="h-12 w-12 text-muted-foreground" />
          </div>
        </div>
        
        <!-- Details Section -->
        <div class="flex-1 p-5 flex flex-col justify-between">
          <div class="flex-1">
            <!-- Test Drive Summary -->
            <div 
              v-if="item.wantsTestDrive && (item.calendarDateValue || item.testDriveTime)"
              class="mb-4 p-3 bg-muted/50 border-b border-border rounded-t-lg"
            >
              <h4 class="text-base font-semibold mb-2 text-foreground">Preferred Test Drive:</h4>
              <div class="flex items-center text-base text-foreground/90">
                <CalendarDaysIcon class="w-4 h-4 mr-2 shrink-0" />
                <span v-if="item.calendarDateValue">{{ format(item.calendarDateValue.toDate(getLocalTimeZone()), 'EEEE, PPP') }}</span>
                <span v-else class="italic text-muted-foreground">Date not selected</span>
                
                <span class="mx-2">-</span> 
                
                <ClockIcon class="w-4 h-4 mr-1.5 shrink-0" />
                <span v-if="item.testDriveTime">{{ item.testDriveTime }}</span>
                <span v-else class="italic text-muted-foreground">Time not selected</span>
              </div>
            </div>
            
            <!-- Top row: Title and Remove Button -->
            <div class="flex items-start justify-between gap-4 mb-3">
              <h3 class="text-2xl font-semibold group-hover:text-primary transition-colors">
                <NuxtLink :to="`/models/${item.modelDetails?.slug}`" class="hover:underline">
                  {{ item.modelDetails?.title.rendered }}
                </NuxtLink>
              </h3>
            </div>
            
            <!-- Segment Tag -->
            <div v-if="item.modelDetails?.segment" class="mb-3">
              <span class="inline-block text-sm px-2.5 py-1 bg-secondary/80 text-secondary-foreground rounded-full font-medium">
                {{ Array.isArray(item.modelDetails.segment) ? item.modelDetails.segment.join(', ') : item.modelDetails.segment }}
              </span>
            </div>
            
            <!-- Caption -->
            <p v-if="item.modelDetails?.caption" class="text-base text-muted-foreground line-clamp-3 mb-5">
              {{ item.modelDetails.caption }}
            </p>
            
            <!-- Enquiry Options - Grouped Section -->
            <div class="border rounded-lg">
              <!-- Test Drive -->
              <div class="p-4">
                <FormField :name="`testdrive-${item.id}`" v-slot="{ field }"> 
                  <FormItem class="flex flex-col space-y-3"> 
                    <div class="flex items-center justify-between"> 
                      <FormLabel class="text-base font-medium">Request a Test Drive</FormLabel>
                      <FormControl>
                        <Switch 
                          :id="`testdrive-${item.id}`" 
                          :model-value="item.wantsTestDrive" 
                          @update:model-value="(val: boolean) => favoritesStore.setTestDrive(item.id, val)" 
                        />
                      </FormControl>
                    </div>
                    <!-- Conditional Date/Time Picker Section -->
                    <div v-if="item.wantsTestDrive" class="flex flex-col gap-3 pt-3"> 
                      <div class="flex-1 space-y-1"> 
                        <FormLabel class="text-sm font-medium text-muted-foreground block pb-1">Preferred Date & Time</FormLabel>
                        <Dialog>
                          <DialogTrigger as-child>
                            <FormControl>
                              <Button
                                variant="outline"
                                :class="cn(
                                  'w-full justify-start text-left font-normal',
                                  !item.calendarDateValue && 'text-muted-foreground',
                                )"
                              >
                                <CalendarIcon class="mr-2 h-4 w-4" />
                                {{ 
                                  item.calendarDateValue 
                                  ? format(item.calendarDateValue.toDate(getLocalTimeZone()), 'EEEE, PPP') + (item.testDriveTime ? ` @ ${item.testDriveTime}` : '') 
                                  : "Pick date & time" 
                                }}
                              </Button>
                            </FormControl>
                          </DialogTrigger>
                          <DialogContent class="sm:max-w-[650px] sm:max-h-[520px] p-0 flex flex-col max-h-[90vh] rounded-xl overflow-hidden">
                            <!-- Dialog Header with Summary -->
                            <DialogHeader class="p-4 border-b bg-muted rounded-t-xl">
                              <DialogTitle class="sr-only">Select Preferred Date & Time</DialogTitle>
                              <DialogDescription class="sr-only">
                                Select a date from the calendar, then choose an available time slot.
                              </DialogDescription>
                              <div v-if="item.calendarDateValue" class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                <div class="flex items-center text-lg font-semibold text-foreground">
                                  <CalendarDaysIcon class="w-5 h-5 mr-2 shrink-0" />
                                  <span>{{ format(item.calendarDateValue.toDate(getLocalTimeZone()), 'EEEE, MMMM d, yyyy') }}</span>
                                </div>
                                <div class="flex items-center text-lg font-semibold sm:border-l sm:border-border sm:pl-4 text-foreground">
                                  <ClockIcon class="w-5 h-5 mr-2 shrink-0" />
                                  <span v-if="item.testDriveTime">{{ item.testDriveTime }}</span>
                                  <span v-else class="text-base italic font-normal text-muted-foreground">Select a time</span>
                                </div>
                              </div>
                               <p v-else class="text-center font-medium text-sm text-muted-foreground">Select a date below</p>
                            </DialogHeader>
                            
                            <!-- Main Dialog Content - Make this scrollable -->
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 p-4 overflow-y-auto flex-1">
                              <!-- Left Column: Calendar -->
                              <div>
                                <Calendar 
                                  :model-value="item.calendarDateValue"
                                  @update:model-value="(date: DateValue | undefined) => {
                                    if (date) {
                                      const nativeDate = date.toDate(getLocalTimeZone());
                                      favoritesStore.setTestDriveDate(item.id, nativeDate.toISOString())
                                    } else {
                                      favoritesStore.setTestDriveDate(item.id, null)
                                    }
                                    // Don't close dialog here anymore, user needs to select time
                                  }" 
                                  initial-focus 
                                />
                              </div>
                              <!-- Right Column: Time Slots -->
                              <div class="md:border-l md:pl-6 flex flex-col">
                                <!-- Time Slot Selection Area -->
                                <div v-if="item.calendarDateValue" class="flex-1">
                                  <div class="space-y-2 pr-2">
                                    <div v-for="time in timeSlots" :key="time" class="flex items-stretch">
                                      <Button 
                                        :variant="item.testDriveTime === time ? 'secondary' : 'outline'"
                                        :class="cn(
                                          'flex-1 justify-center h-10', 
                                          item.testDriveTime === time && 'ring-2 ring-ring ring-offset-2'
                                        )"
                                        @click="favoritesStore.setTestDriveTime(item.id, time)"
                                      >
                                        {{ time }}
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                                <div v-else class="flex-1 flex items-center justify-center">
                                  <p class="text-sm text-muted-foreground text-center">Select a date to see available times.</p>
                                </div>
                              </div>
                            </div>
                            <!-- Dialog Footer -->
                            <div class="flex justify-end p-4 border-t">
                              <DialogClose as-child>
                                <Button 
                                  variant="default"
                                  :disabled="!item.calendarDateValue || !item.testDriveTime"
                                  @click="confirmTime(item.id)"
                                >
                                  Confirm Booking
                                </Button>
                              </DialogClose>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </div>
                  </FormItem>
                </FormField>
              </div>
              
              <!-- Trade-in -->
              <FormField :name="`tradein-${item.id}`" v-slot="{ field }"> 
                <FormItem class="flex flex-col border-t p-4 space-y-3">
                  <div class="flex flex-row items-center justify-between">
                    <FormLabel class="text-base font-medium grow mr-4">Get an estimate for my Trade-in</FormLabel>
                    <FormControl>
                      <Switch 
                        :id="`tradein-${item.id}`" 
                        :model-value="item.hasTradeIn" 
                        @update:model-value="(val: boolean) => favoritesStore.setTradeIn(item.id, val)"
                      />
                    </FormControl>
                  </div>
                  <!-- Conditional Trade-in Detail Fields -->
                  <div v-if="item.hasTradeIn" class="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2"> 
                    <!-- Make -->
                    <FormField :name="`tradeInMake-${item.id}`" v-slot="{ field: makeField }">
                      <FormItem>
                        <FormControl>
                          <Input 
                            type="text" 
                            placeholder="Make (e.g., Toyota)"
                            :model-value="item.tradeInMake"
                            @update:model-value="(val: string) => favoritesStore.setTradeInMake(item.id, val)"
                          />
                        </FormControl>
                      </FormItem>
                    </FormField>
                    <!-- Model -->
                    <FormField :name="`tradeInModel-${item.id}`" v-slot="{ field: modelField }">
                      <FormItem>
                        <FormControl>
                          <Input 
                            type="text" 
                            placeholder="Model (e.g., Camry)"
                            :model-value="item.tradeInModel"
                            @update:model-value="(val: string) => favoritesStore.setTradeInModel(item.id, val)"
                           />
                        </FormControl>
                      </FormItem>
                    </FormField>
                    <!-- Year -->
                    <FormField :name="`tradeInYear-${item.id}`" v-slot="{ field: yearField }">
                      <FormItem>
                        <FormControl>
                          <Input 
                            type="number" 
                            placeholder="Year (e.g., 2019)"
                            :model-value="item.tradeInYear"
                            @update:model-value="(val: string) => favoritesStore.setTradeInYear(item.id, val)"
                          />
                        </FormControl>
                      </FormItem>
                    </FormField>
                    <!-- Kms -->
                    <FormField :name="`tradeInKms-${item.id}`" v-slot="{ field: kmsField }">
                      <FormItem>
                        <FormControl>
                          <Input 
                            type="number" 
                            placeholder="Kilometers (approx)"
                            :model-value="item.tradeInKms"
                            @update:model-value="(val: string) => favoritesStore.setTradeInKms(item.id, val)"
                           />
                        </FormControl>
                      </FormItem>
                    </FormField>
                  </div>
                </FormItem>
              </FormField>
              
              <!-- Finance -->
              <FormField :name="`finance-${item.id}`" v-slot="{ field }"> 
                <FormItem class="flex flex-row items-center justify-between border-t p-4"> 
                  <FormLabel class="text-base font-medium grow mr-4">Explore Finance Options</FormLabel>
                  <FormControl>
                    <Switch 
                      :id="`finance-${item.id}`" 
                      :model-value="item.interestedInFinance"
                      @update:model-value="(val: boolean) => favoritesStore.setFinance(item.id, val)"
                    />
                  </FormControl>
                </FormItem>
              </FormField>
            </div>

            <!-- Comment Textarea -->
            <div class="mt-auto">
              <label :for="`comment-${item.id}`" class="block text-base font-medium text-foreground mt-4 mb-1">Your Notes:</label>
              <textarea 
                :id="`comment-${item.id}`" 
                rows="3" 
                :value="item.comment" 
                @input="updateCommentDebounced(item.id, ($event.target as HTMLTextAreaElement).value)"
                class="block w-full px-3 py-2 border border-input rounded-md shadow-xs focus:outline-hidden focus:ring-1 focus:ring-ring sm:text-sm bg-background"
                placeholder="Add specific questions or details..."
              ></textarea>
            </div>
          </div>
        </div>
      </div>

      <!-- Scroll to Enquiry Button -->
      <div v-if="enrichedFavoriteModels.length > 0" class="text-center mt-10 mb-6">
        <Button 
          size="lg" 
          @click="scrollToEnquiry"
          class="rounded-full px-8 py-3 text-lg"
        >
          Continue with your enquiry
        </Button>
      </div>

      <!-- Enquiry Section - Visually Separated -->
      <div id="enquiry-form" class="mt-16 pt-10 pb-8 px-6 bg-muted/40 rounded-lg border">
        <div class="max-w-xl mx-auto">
          <h2 class="text-2xl font-semibold mb-3 text-center">Ready to Enquire?</h2>
          <p class="text-muted-foreground mb-6 text-center text-sm">Submit your details below and our team will contact you regarding your selected models and options.</p>
          <FavoritesEnquiryForm :favorites="favoritesStore.favorites" /> 
        </div>
      </div>
    </div>

    <div v-else class="text-center py-16">
      <HeartIcon class="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
      <p class="text-xl font-medium text-foreground mb-2">Your favorites list is empty.</p>
      <p class="text-base text-muted-foreground mb-6">Add models you're interested in to easily compare and enquire.</p>
      <NuxtLink 
        to="/models" 
        class="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors"
      >
        Browse All Models
      </NuxtLink>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useFavoritesStore, type FavoriteItem } from '~/stores/favorites'
import { useModelsStore, type VehicleModel } from '~/stores/models'
import { PhotoIcon, CalendarDaysIcon, ClockIcon, ChevronDownIcon, XMarkIcon, HeartIcon } from '@heroicons/vue/24/outline'
import { CalendarIcon } from "lucide-vue-next";
import { useDebounceFn } from "@vueuse/core";
import Switch from "~/layers/base/components/ui/switch/Switch.vue";
import Input from "~/layers/base/components/ui/input/Input.vue";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '~/layers/base/components/ui/form/index'
import Calendar from '~/layers/base/components/ui/calendar/Calendar.vue'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/layers/base/components/ui/dialog/index'
import Button from '~/layers/base/components/ui/button/Button.vue'
import { cn } from '~/lib/utils'
import { format, parseISO } from 'date-fns'
import { useForm } from 'vee-validate'
import { type DateValue, getLocalTimeZone, CalendarDate } from '@internationalized/date'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/layers/base/components/ui/select/index'

definePageMeta({
  // Add middleware or layout if needed
})

useForm()

const favoritesStore = useFavoritesStore()
const modelsStore = useModelsStore()
const isLoading = ref(true)

// Define available time slots in 12-hour format
const timeSlots = ref([
  '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
  '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM',
  '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM', '5:00 PM'
])

// Combine favorite item data with model details
const enrichedFavoriteModels = computed(() => {
  return favoritesStore.favorites.map(favItem => {
    const modelDetails = modelsStore.models.find(m => m.id.toString() === favItem.id)
    
    // Convert ISO string to CalendarDate for the :model-value binding
    const initialDate = favItem.testDriveDateTime ? parseISO(favItem.testDriveDateTime) : null;
    const calendarDateValue = initialDate 
      ? new CalendarDate(initialDate.getFullYear(), initialDate.getMonth() + 1, initialDate.getDate()) 
      : undefined;
      
    return {
      ...favItem,
      modelDetails: modelDetails || null,
      calendarDateValue // Add the converted value to the item object for template access
    }
  })
  // Keep items even if modelDetails are temporarily null during loading/updates
  // Filtering happens implicitly in the template v-if checks
})

// Fetch model details for favorited IDs
onMounted(async () => {
  isLoading.value = true
  try {
    if (modelsStore.models.length === 0) {
      // console.log('Favorites page: Models not loaded, fetching...');
      await modelsStore.fetchModels()
    }
  } catch (error) {
    // console.error("Error loading favorite models:", error)
    // Handle error display, maybe set an error ref
  } finally {
    isLoading.value = false
  }
})

const updateCommentDebounced = useDebounceFn((modelId: string, comment: string) => {
  favoritesStore.updateComment(modelId, comment)
}, 500)

// Basic function for confirmation button click
function confirmTime(itemId: string) {
  // console.log(\`Time confirmed for item ${itemId}: ${favoritesStore.favorites.find(f => f.id === itemId)?.testDriveTime}\`);
  // Later: Close the dialog, potentially trigger API call, etc.
}

// Function to scroll to the enquiry form
function scrollToEnquiry() {
  const formElement = document.getElementById('enquiry-form');
  if (formElement) {
    formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

</script> 